import { useEffect, useRef, useState } from "react";
import { Accessibility, X, Type, Contrast, BookOpenText, Link2, Pause, RotateCcw, Check, Glasses } from "lucide-react";
import { useA11y } from "./A11yProvider";
import { useT } from "@/i18n/I18nProvider";

const TOGGLES = [
  { key: "easyRead", icon: Glasses, label: "Easy Read", hint: "Simple words and short sentences", testId: "a11y-easy-read" },
  { key: "contrast", icon: Contrast, label: "High contrast", hint: "Stronger colours and borders", testId: "a11y-contrast" },
  { key: "readableFont", icon: BookOpenText, label: "Readable font", hint: "Easier-to-read letter shapes", testId: "a11y-font" },
  { key: "links", icon: Link2, label: "Highlight links", hint: "Underline and emphasise links", testId: "a11y-links" },
  { key: "motion", icon: Pause, label: "Reduce motion", hint: "Turn off animations", testId: "a11y-motion" },
];
const SIZE_LABELS = ["Default", "Large", "Extra large"];

const Toggle = ({ icon: Icon, label, hint, on, onClick, testId }) => (
  <button type="button" role="switch" aria-checked={on} onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-start transition-colors ${on ? "bg-[var(--blue)] border-[var(--blue)] text-white" : "bg-white border-slate-200 hover:border-[var(--blue)]"}`}>
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${on ? "bg-white/15" : "bg-[var(--blue-tint)] text-[var(--blue)]"}`}><Icon size={20} /></span>
    <span className="flex-1">
      <span className="block font-heading font-bold text-sm" style={{ color: "inherit" }}>{label}</span>
      <span className={`block text-xs ${on ? "text-white/75" : "text-[var(--muted)]"}`}>{hint}</span>
    </span>
    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${on ? "border-white bg-white text-[var(--blue)]" : "border-slate-300"}`}>{on && <Check size={14} strokeWidth={3} />}</span>
  </button>
);

const TextSizeControl = ({ size, onChange }) => {
  const t = useT();
  return (
    <div className="p-3 rounded-2xl border border-slate-200">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[var(--blue-tint)] text-[var(--blue)] flex items-center justify-center"><Type size={20} /></span>
        <div className="flex-1"><div className="font-heading font-bold text-sm">{t("Text size")}</div><div className="text-xs text-[var(--muted)]" aria-live="polite">{t(SIZE_LABELS[size])}</div></div>
        <div className="flex items-center gap-1" role="group" aria-label={t("Text size")}>
          <button type="button" aria-label={t("Decrease text size")} data-testid="a11y-text-smaller" disabled={size === 0} onClick={() => onChange(Math.max(0, size - 1))} className="w-9 h-9 rounded-full border border-slate-200 font-bold disabled:opacity-40 hover:bg-[var(--blue-tint)]">A-</button>
          <button type="button" aria-label={t("Increase text size")} data-testid="a11y-text-larger" disabled={size === 2} onClick={() => onChange(Math.min(2, size + 1))} className="w-9 h-9 rounded-full border border-slate-200 font-bold disabled:opacity-40 hover:bg-[var(--blue-tint)]">A+</button>
        </div>
      </div>
    </div>
  );
};

const Panel = ({ panelRef, onClose }) => {
  const t = useT();
  const { prefs, set, reset } = useA11y();
  return (
    <div ref={panelRef} role="dialog" aria-modal="false" aria-label={t("Accessibility options")} data-testid="a11y-panel"
      className="mb-3 w-[320px] max-w-[calc(100vw-2rem)] rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/20 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold flex items-center gap-2"><Accessibility size={18} className="text-[var(--blue)]" /> {t("Accessibility")}</h2>
        <button type="button" onClick={onClose} aria-label={t("Close accessibility options")} data-testid="a11y-close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={18} /></button>
      </div>
      <div className="space-y-2">
        <TextSizeControl size={prefs.textSize} onChange={(v) => set("textSize", v)} />
        {TOGGLES.map((tg) => <Toggle key={tg.key} icon={tg.icon} label={t(tg.label)} hint={t(tg.hint)} on={prefs[tg.key]} onClick={() => set(tg.key, !prefs[tg.key])} testId={tg.testId} />)}
      </div>
      <button type="button" onClick={reset} data-testid="a11y-reset" className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-heading font-bold text-[var(--muted)] hover:text-[var(--blue)] py-2"><RotateCcw size={14} /> {t("Reset to defaults")}</button>
    </div>
  );
};

export const AccessibilityWidget = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  // Deps: refs are stable and setOpen is a state setter; `open` is the only reactive value.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector("button")?.focus();
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => { setOpen(false); btnRef.current?.focus(); };

  return (
    <div className="fixed start-4 bottom-4 z-[90]" data-testid="a11y-widget">
      {open && <Panel panelRef={panelRef} onClose={close} />}
      <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={t("Accessibility options")} data-testid="a11y-toggle"
        className="w-14 h-14 rounded-full bg-[var(--blue)] text-white shadow-xl shadow-blue-900/30 flex items-center justify-center hover:bg-[var(--blue-dark)] hover:scale-105 transition-all focus-visible:ring-4 focus-visible:ring-[var(--coral)]">
        <Accessibility size={26} />
      </button>
    </div>
  );
};
